<script setup lang="ts">
import type { SizeTypes } from "@/types";
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { nanoid } from "nanoid";
import type { DefaultInputProps } from "@/interfaces";
import { onMounted, ref, toRefs } from "vue";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import InlineLoading from "@/stories/displays/InlineLoading/InlineLoading.vue";

const id = nanoid();
type InputTypes = "text" | "password" | "email" | "number";

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  labelRight?: string;
  type?: InputTypes;
  size?: SizeTypes;
  modelValue?: string | number;
  icon?: string;
  rounded?: boolean;
  maxlength?: number;
  background?: string;
  customClasses?: string;
  numberOnly?: boolean;
  autoFocus?: boolean;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  size: "md",
  background: "bg-neutral0",
  customClasses: "",
  numberOnly: false,
  autoFocus: false,
});

const inputRef = ref<HTMLInputElement>();
const { name, validation, numberOnly, autoFocus } = toRefs(props);

const emit = defineEmits(["update:modelValue", "blur"]);

const SIZE_STYLES = {
  sm: "py-2 px-4",
  md: "p-4",
  lg: "p-5",
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;

  if (validation.value && validation.value.errors) {
    delete validation.value.errors[name.value];
  }

  // Only allow numbers but without using type number
  if (numberOnly.value) {
    // Remove all non-digit characters
    const numericValue = target.value.replace(/\D/g, "");
    target.value = numericValue;

    // Emit the parsed integer, or 0 if empty
    emit(
      "update:modelValue",
      numericValue === "" ? 0 : parseInt(numericValue, 10),
    );
    return;
  }

  emit("update:modelValue", (event.target as HTMLInputElement).value);
};

const { hasError } = useFieldValidationError(name, validation);

onMounted(async () => {
  if (autoFocus.value && inputRef.value) {
    const timeout = setTimeout(() => {
      clearTimeout(timeout);
      if (inputRef.value) {
        inputRef.value.focus();
      }
    }, 100);
  }
});
</script>

<template>
  <InputWrapper
    :forId="id"
    :label="label"
    :description="description"
    :isRequired="isRequired"
    :inputKey="inputKey"
    :tooltip="tooltip"
    :disabled="disabled"
    :name="name"
    :validation="validation"
    :errorMessageKey="errorMessageKey"
  >
    <div
      class="relative w-full"
      :class="{
        'text-neutral600': disabled,
        'flex items-center': labelRight?.length,
      }"
    >
      <div class="flex justify-between gap-2">
        <input
          ref="inputRef"
          :id="id"
          :type="type"
          :value="modelValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxlength"
          :class="[
            'transition duration-200 text-base text-neutral1000 input focusVisible b300 px-4 py-3',
            'disabled:text-neutral600',
            'outline outline-1 outline-neutral500',
            'focus:outline-2 focus:enabled:outline-neutral1000',
            'placeholder:text-neutral700 placeholder:font-normal placeholder:tracking-tight placeholder:b300',
            {
              'outline outline-1 outline-red400': hasError,
              'rounded-full': rounded,
              'rounded-lg': !rounded,
              'pl-12': icon,
              'mr-4': labelRight?.length,
              'w-full': !labelRight?.length,
              [background]: true,
              [SIZE_STYLES[size]]: true,
              [customClasses]: true,
            },
          ]"
          @input="handleInput"
          @blur="$emit('blur')"
        />
        <slot name="input-right"></slot>
      </div>
      <div
        v-if="helpText"
        class="absolute bottom-[-50px] mx-auto w-full flex justify-center"
      >
        <div
          :class="[
            'bg-black text-white b300 px-4 py-2 shadow-small rounded-md z-50',
            `before:content-[''] before:absolute before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-black`,
          ]"
        >
          {{ helpText }}
        </div>
      </div>
      <p class="h200" v-if="labelRight?.length">{{ labelRight }}</p>
      <div
        v-if="icon"
        class="absolute left-0 top-0 h-[100%] w-[3.375rem] flex justify-center items-center"
      >
        <i class="text-neutral700" :class="[icon, size]"></i>
      </div>
      <InlineLoading v-if="inlineLoading" />
    </div>
  </InputWrapper>
</template>
